/**
 * @file Stats.tsx
 * @description
 * Displays summary vote statistics for the currently selected bill.
 *
 * This component reads aggregate statistics from the global data store
 * (via `useDataStore`) and shows aggregate counts for Yea, No Vote, and Nay,
 * along with metric statistics when available.
 *
 * The data automatically reflects the active bill because `districts`
 * in the store is updated via `loadBillFromCongress()`.
 */

import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useDataStore, useVizStore } from '@/store';

export default function Stats() {
  // Pull metric aggregates from global store (includes vote counts)
  const metricAggregates = useDataStore((s) => s.metricAggregates);
  const metricKey = useDataStore((s) => s.metricKey);
  const metricStatistics = useDataStore((s) => s.metricStatistics);
  const billId = useDataStore((s) => s.billId);
  const findInMetricIndex = useDataStore((s) => s.findInMetricIndex);

  // Get vote type colors from viz store with fallbacks
  const voteSupportColor = useVizStore((s) => s.voteSupportColor) || '#2e7d32';
  const voteAgainstColor = useVizStore((s) => s.voteAgainstColor) || '#cccc1b';
  const voteAbsenceColor = useVizStore((s) => s.voteAbsenceColor) || '#EEEEEE';

  // Use vote counts from aggregates, or default to 0 if not available
  const yeaCount = metricAggregates?.yeaCount ?? 0;
  const noVoteCount = metricAggregates?.noVoteCount ?? 0;
  const nayCount = metricAggregates?.nayCount ?? 0;

  // Only show metric averages if a metric is selected
  const showMetricAverages = metricKey && metricAggregates;

  // Get metric label from metric index
  const metricLabel = metricKey ? findInMetricIndex(metricKey)?.title : null;
  const avgMetricLabel = metricLabel ? `Avg ${metricLabel}` : 'Avg Metric';

  console.log('billId: ', billId);
  console.log('metric stats: ', metricStatistics);

  const statsBundle = billId ? metricStatistics?.[billId.toLowerCase()] : undefined;

  //WELCOME TO HELL
  let subTitle = 'YOU, yes YOU, broke something';
  if (statsBundle?.significant?.[0] === 'Yes') {
    if ((metricAggregates?.voteSupportMetricAvg ?? 0) > (metricAggregates?.metricAvg ?? 0)) {
      subTitle = `Compared to the overall average, congresspeople who voted Yea had a significantly higher ${metricLabel}`;
    } else {
      subTitle = `Compared to the overall average, congresspeople who voted Yea had a significantly lower ${metricLabel}`;
    }
  } else {
    subTitle = ` Compared to the overall average, congresspeople who voted Yea did not have a significantly different ${metricLabel}`;
  }

  if (statsBundle?.significant?.[1] === 'Yes') {
    if ((metricAggregates?.voteAgainstMetricAvg ?? 0) > (metricAggregates?.metricAvg ?? 0)) {
      if (subTitle.includes('higher')) {
        subTitle += `; likewise, congresspeople who voted Nay were significantly higher.`;
      } else {
        subTitle += `, while congresspeople who voted Nay were significantly higher.`;
      }
    } else {
      if (subTitle.includes('lower')) {
        subTitle += `; likewise, congresspeople who voted Nay were significantly lower.`;
      } else {
        subTitle += `, while congresspeople who voted Nay were significantly lower.`;
      }
    }
  } else {
    if (subTitle.includes('did not')) {
      subTitle += `; likewise, congresspeople who voted Nay did not have a significantly different ${metricLabel}.`;
    } else {
      subTitle += `, while congresspeople who voted Nay did not have a significantly different ${metricLabel}.`;
    }
  }

  //console.log(`(t = ${t}, p ≈ ${p}, df = ${df})`)

  return (
    <Card className="border-0 shadow-sm mb-3" aria-live="polite">
      <Card.Header className="bg-white fw-bold border-0 pb-0 h4">STATISTICS</Card.Header>

      <Card.Body className="py-3">
        {/*Summary subheader*/}
        <div>{`${subTitle}`}</div>
        <div></div>
        <Row className="g-3">
          {/* Yea panel */}
          <Col xs={12} md={4}>
            <div className="h-100 rounded-2 border mt-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-bold">Yea</div>
                <span
                  className="rounded-circle d-inline-block"
                  style={{ width: 10, height: 10, background: voteSupportColor }}
                  aria-hidden
                />
              </div>
              <Row className="text-center g-2">
                <Col>
                  <div className="text-muted small">Count</div>
                  <div className="fs-3">{yeaCount}</div>
                </Col>
                {showMetricAverages && (
                  <Col>
                    <div className="text-muted small">{avgMetricLabel}</div>
                    <div className="fs-3">
                      {metricAggregates.voteSupportMetricAvg.toLocaleString('en-US')}
                    </div>
                  </Col>
                )}
                <Col>
                  <div className="text-muted small">
                    <details>
                      <summary>Significant?</summary>
                      {(() => {
                        if (statsBundle) {
                          const t = statsBundle.tstat?.[0];
                          const p = statsBundle.p?.[0];
                          const df = statsBundle.df?.[0];
                          return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                        }
                        const t = metricAggregates?.voteSupportMetricTtest?.[0];
                        const p = metricAggregates?.voteSupportMetricTtest?.[1];
                        const df = metricAggregates?.voteSupportMetricTtest?.[2];
                        return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                      })()}
                    </details>
                  </div>
                  <div className="fs-3">
                    {(() => {
                      const statsBundle = billId ? metricStatistics?.[billId] : undefined;
                      if (statsBundle && statsBundle.significant?.[0] !== undefined) {
                        return statsBundle.significant[0];
                      }
                      const p = metricAggregates?.voteSupportMetricTtest?.[1] ?? 1;
                      return p < 0.05 ? 'Yes' : 'No';
                    })()}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Nay panel */}
          <Col xs={12} md={4}>
            <div className="h-100 rounded-2 border mt-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-bold">Nay</div>
                <span
                  className="rounded-circle d-inline-block"
                  style={{ width: 10, height: 10, background: voteAgainstColor }}
                  aria-hidden
                />
              </div>
              <Row className="text-center g-2">
                <Col>
                  <div className="text-muted small">Count</div>
                  <div className="fs-3">{nayCount}</div>
                </Col>
                {showMetricAverages && (
                  <Col>
                    <div className="text-muted small">{avgMetricLabel}</div>
                    <div className="fs-3">
                      {metricAggregates.voteAgainstMetricAvg.toLocaleString('en-US')}
                    </div>
                  </Col>
                )}
                <Col>
                  <div className="text-muted small">
                    <details>
                      <summary>Significant?</summary>
                      {(() => {
                        if (statsBundle) {
                          const t = statsBundle.tstat?.[1];
                          const p = statsBundle.p?.[1];
                          const df = statsBundle.df?.[1];
                          return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                        }
                        const t = metricAggregates?.voteAgainstMetricTtest?.[0];
                        const p = metricAggregates?.voteAgainstMetricTtest?.[1];
                        const df = metricAggregates?.voteAgainstMetricTtest?.[2];
                        return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                      })()}
                    </details>
                  </div>
                  <div className="fs-3">
                    {(() => {
                      if (statsBundle && statsBundle.significant?.[1] !== undefined) {
                        return statsBundle.significant[1];
                      }
                      const p = metricAggregates?.voteAgainstMetricTtest?.[1] ?? 1;
                      return p < 0.05 ? 'Yes' : 'No';
                    })()}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Undefined Vote panel */}
          <Col xs={12} md={4}>
            <div className="h-100 rounded-2 border mt-2 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-bold">No Vote</div>
                <span
                  className="rounded-circle d-inline-block"
                  style={{ width: 10, height: 10, background: voteAbsenceColor }}
                  aria-hidden
                />
              </div>
              <Row className="text-center g-2">
                <Col>
                  <div className="text-muted small">Count</div>
                  <div className="fs-3">{noVoteCount}</div>
                </Col>
                {showMetricAverages && (
                  <Col>
                    <div className="text-muted small">{avgMetricLabel}</div>
                    <div className="fs-3">
                      {metricAggregates.voteAbsenceMetricAvg.toLocaleString('en-US')}
                    </div>
                  </Col>
                )}
                <Col>
                  <div className="text-muted small">
                    <details>
                      <summary>Significant?</summary>
                      {(() => {
                        if (statsBundle) {
                          const t = statsBundle.tstat?.[2];
                          const p = statsBundle.p?.[2];
                          const df = statsBundle.df?.[2];
                          return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                        }
                        const t = metricAggregates?.voteAbsenceMetricTtest?.[0];
                        const p = metricAggregates?.voteAbsenceMetricTtest?.[1];
                        const df = metricAggregates?.voteAbsenceMetricTtest?.[2];
                        return ` (t = ${t}, p ≈ ${p}, df = ${df})`;
                      })()}
                    </details>
                  </div>
                  <div className="fs-3">
                    {(() => {
                      if (statsBundle && statsBundle.significant?.[2] !== undefined) {
                        return statsBundle.significant[2];
                      }
                      const p = metricAggregates?.voteAbsenceMetricTtest?.[1] ?? 1;
                      return p < 0.05 ? 'Yes' : 'No';
                    })()}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
